/**
 * Papier Royal — Customily Strip Orphan (garde anti-perso-fantôme)
 *
 * BUG (commandes #2175, #2125 — juin 2026) :
 *   La perso payante d'un carnet = une LIGNE SÉPARÉE « Extra - Dorure / Gilding »
 *   (prix par caractère), liée à la ligne carnet via :
 *       carnet.properties['_customily-upsell-main-id'] === gilding.properties['_customily-upsell-id']
 *   Quand le client retire la perso (« trop cher »), Customily supprime la ligne
 *   Gilding, mais la ligne carnet GARDE ses métadonnées orphelines :
 *       _customily-* (dont _customily-production-url), Position, Your Initials.
 *   → Panier + commande montrent une perso à produire non payée.
 *
 * SIGNAL FIABLE (confirmé sur commandes réelles) :
 *   Orphelin = ligne avec `_customily-upsell-main-id` = X SANS ligne sœur
 *   `_customily-upsell-id` = X. (#2175 #2125 orphelins ; #2049 #1986 appairés OK.)
 *   ⚠ La présence/absence d'initiales n'est PAS fiable (« Your Initials: ABC »
 *     reste collé après retrait — observé en QA).
 *
 * CORRECTIF — réconciliateur panier :
 *   On nettoie chaque ligne orpheline. IMPORTANT : `/cart/change.js` avec
 *   `properties: {}` est IGNORÉ par Shopify (vérifié en QA — la ligne reste
 *   inchangée). On fait donc SUPPRIMER (quantity 0) + RÉ-AJOUTER la même variante
 *   avec les propriétés nettoyées (perso retirée). La ligne redevient un carnet nu.
 *
 * Déclencheurs :
 *   - Au chargement (silencieux — pas d'auto-open du drawer).
 *   - Wrapper window.fetch : après tout /cart/change|update|add.js (y compris la
 *     suppression silencieuse de la Dorure par Customily, qui n'émet pas
 *     cart:update), on planifie une réconciliation (debounced).
 *   - Events cart:update / cart:added.
 *
 * Garde anti-boucle : max 2 tentatives par variante (au cas où Customily
 *   ré-injecterait sur le ré-ajout — non observé, mais on borne).
 */

(function () {
  'use strict';

  var DEBUG = false;
  function log() {
    if (DEBUG) console.log.apply(console, ['[PR strip-orphan]'].concat([].slice.call(arguments)));
  }

  var CUSTOMILY_PREFIX = '_customily-';
  var KEY_MAIN = '_customily-upsell-main-id';
  var KEY_UPSELL = '_customily-upsell-id';
  var DEBOUNCE_MS = 1500;
  var MAX_ATTEMPTS = 2;

  function isPositionKey(k) {
    return String(k).trim().toLowerCase() === 'position';
  }
  // Clés monogramme « invisibles » de Customily (espaces + zero-width + BOM)
  function isWhitespaceKey(k) {
    return /^[\s\u200B\u200C\u200D\uFEFF]+$/.test(String(k));
  }
  function isInitialsKey(k) {
    return /initial|initiale/i.test(String(k)) || /make it your own/i.test(String(k));
  }

  // Propriétés nettoyées : on retire toute trace de perso Customily, on garde
  // d'éventuelles propriétés non-perso (ex : message cadeau).
  function cleanProps(props) {
    var out = {};
    Object.keys(props || {}).forEach(function (k) {
      if (k.indexOf(CUSTOMILY_PREFIX) === 0) return;
      if (isPositionKey(k)) return;
      if (isWhitespaceKey(k)) return;
      if (isInitialsKey(k)) return;
      out[k] = props[k];
    });
    return out;
  }

  function getMorphableSections() {
    var sections = new Set();
    document.querySelectorAll('[data-section-id], [id^="shopify-section-"]').forEach(function (el) {
      var id = el.dataset.sectionId || el.id.replace('shopify-section-', '');
      if (
        id &&
        (el.querySelector('[data-cart-companions-mount]') ||
          el.querySelector('cart-drawer-component') ||
          id.indexOf('header') !== -1)
      ) {
        sections.add(id);
      }
    });
    return Array.from(sections).join(',');
  }

  var running = false;
  var quietUntil = 0; // ignore les events déclenchés par nos propres requêtes
  var attempts = {}; // variantId -> nb tentatives (garde anti-boucle)

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  // Customily wrappe window.fetch et RÉ-INJECTE la perso (_customily-* + Position)
  // sur tout /cart/add.js du top window (confirmé : remove+readd en live recrée
  // l'orphelin, alors qu'au reload — configurateur pas encore actif — c'est propre).
  // Parade : on fait nos mutations panier via le fetch d'un iframe same-origin, que
  // Customily n'a pas wrappé → aucune interception. URLs absolues (le base URL d'un
  // iframe about:blank ne résout pas les chemins relatifs).
  var _iframe = null;
  function cleanFetch(path, opts) {
    var abs = window.location.origin + path;
    try {
      if (!_iframe) {
        _iframe = document.createElement('iframe');
        _iframe.style.display = 'none';
        _iframe.setAttribute('aria-hidden', 'true');
        (document.body || document.documentElement).appendChild(_iframe);
      }
      var f = _iframe.contentWindow && _iframe.contentWindow.fetch;
      if (f) return f.call(_iframe.contentWindow, abs, opts);
    } catch (e) {
      log('iframe fetch indispo, fallback', e);
    }
    return fetch(abs, opts);
  }

  /**
   * @param {boolean} silent  true au chargement → ne PAS dispatcher cart:update
   *   (sinon cart-companions auto-ouvre le drawer de façon intempestive).
   */
  async function reconcile(silent) {
    if (running) return;
    running = true;
    try {
      var cart = await getCart();
      var items = (cart && cart.items) || [];

      var presentUpsellIds = new Set();
      items.forEach(function (it) {
        var u = it.properties && it.properties[KEY_UPSELL];
        if (u) presentUpsellIds.add(u);
      });

      var orphans = items.filter(function (it) {
        var mainId = it.properties && it.properties[KEY_MAIN];
        if (!mainId || presentUpsellIds.has(mainId)) return false;
        return (attempts[it.variant_id] || 0) < MAX_ATTEMPTS;
      });
      if (!orphans.length) {
        log('rien à réconcilier');
        return;
      }
      log('orphelins:', orphans.length, silent ? '(silent)' : '');

      quietUntil = Date.now() + DEBOUNCE_MS + 1500;
      var lastSections = null;

      for (var i = 0; i < orphans.length; i++) {
        var it = orphans[i];
        attempts[it.variant_id] = (attempts[it.variant_id] || 0) + 1;
        var cleaned = cleanProps(it.properties);

        // 1. Supprimer la ligne orpheline (properties:{} étant ignoré par Shopify,
        //    on ne peut pas vider en place → suppression).
        var del = await cleanFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: it.key, quantity: 0 })
        });
        if (!del.ok) {
          log('échec suppression', it.key, del.status);
          continue;
        }

        // 2. Ré-ajouter la même variante, propre (sans perso).
        var addBody = {
          items: [
            {
              id: it.variant_id,
              quantity: it.quantity,
              properties: Object.keys(cleaned).length ? cleaned : undefined
            }
          ],
          sections: getMorphableSections(),
          sections_url: window.location.pathname
        };
        var add = await cleanFetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(addBody)
        });
        if (add.ok) {
          var data = await add.json();
          lastSections = data && data.sections ? data.sections : lastSections;
          log('ligne nettoyée (remove+readd):', it.variant_id, '→', cleaned);
        } else {
          log('échec ré-ajout', it.variant_id, add.status);
        }
      }

      // 3. Rafraîchir le drawer — seulement hors chargement initial.
      //    IMPORTANT : on NE passe PAS `sections`. Sans sections, component-cart-items
      //    tombe dans son fallback `sectionRenderer.renderSection(..., {cache:false})`
      //    qui RE-FETCH le drawer frais (HTML serveur = panier nettoyé). Passer les
      //    sections du /cart/add appliquait un HTML stale (Position restait affichée).
      if (!silent) {
        var fresh = await getCart();
        var fireRefresh = function () {
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: fresh,
                sourceId: 'pr-strip-orphan',
                data: { source: 'pr-strip-orphan', itemCount: fresh.item_count }
              }
            })
          );
        };
        fireRefresh();
        // 2e passe différée : gagne la course si Customily re-render le drawer
        // (avec sa Position périmée) juste après notre nettoyage.
        setTimeout(fireRefresh, 900);
      }
    } catch (e) {
      log('reconcile error', e);
    } finally {
      running = false;
    }
  }

  // ---------- Déclencheur principal : wrapper fetch ----------
  // Customily retire la Dorure via /cart/change.js SANS émettre l'event cart:update
  // du thème. On observe donc directement les mutations panier réseau.

  var timer = null;
  function schedule(silent) {
    clearTimeout(timer);
    timer = setTimeout(function () {
      reconcile(silent);
    }, DEBOUNCE_MS);
  }

  // [LOT A — 2026-07-06] Wrapper global de window.fetch RETIRÉ (cause de S6 « Failed to fetch »).
  // Réconciliation conservée via events cart:* + au chargement ; intégrité garantie server-side.

  // ---------- Déclencheurs events ----------
  ['cart:update', 'cart:added', 'product:added-to-cart'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      if (
        e &&
        e.detail &&
        (e.detail.sourceId === 'pr-strip-orphan' || (e.detail.data && e.detail.data.source === 'pr-strip-orphan'))
      ) {
        return;
      }
      if (Date.now() < quietUntil) return;
      schedule(false);
    });
  });

  // ---------- Réconciliation initiale (silencieuse) ----------
  function initial() {
    reconcile(true);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initial);
  } else {
    initial();
  }

  window.PRStripOrphan = { reconcile: reconcile };
})();
